import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiClient } from '../services/apiClient';
import { Button } from './Button';
import { useAuth } from '../hooks/useAuth';
import {
  Section, SectionLabel, PresetGrid, PresetCard, NewStyleCard, DeleteStyleBtn,
  EditStyleBtn, CustomBadge, PromptTextarea, DropZone, PreviewImg,
  HiddenInput, Actions, ErrorBox, ModalOverlay, ModalBox, TextInput,
  ModeTabs, ModeTab, Hint, Panel, SampleImg, MutedNote, SuccessNote,
  NoThumb, EditThumb, ThumbImg, Row,
} from './styleManager/styled';
import { VariablePicker } from './styleManager/VariablePicker';

// Gestión de ESTILOS (uno de los dos pilares de generación, junto con Templates).
// Antes vivía dentro de "Photo Stylize"; se desacopló para que crear/editar/probar
// estilos tenga su propio lugar. Los estilos se usan en todo el resto de la
// plataforma (QuickGen, Novah, baked ads) para generar piezas.

interface PresetSummary {
  id: string;
  label: string;
  description: string;
  defaultAspectRatio: string;
  previewPath: string;
  custom?: boolean;
  createdBy?: string | null;
}
interface PresetsResponse { presets: PresetSummary[] }
interface StylizeStatusResponse { state: 'waiting' | 'success' | 'fail'; resultUrl?: string; error?: string }

interface StyleManagerProps {
  workspaceId: string;
}

export function StyleManager({ workspaceId }: StyleManagerProps) {
  const { user } = useAuth();
  const [presets, setPresets] = useState<PresetSummary[]>([]);
  const [error, setError] = useState('');

  // Crear estilo.
  const [showNewStyle, setShowNewStyle] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [newStyleMode, setNewStyleMode] = useState<'ai' | 'manual'>('ai');
  const [styleFile, setStyleFile] = useState<File | null>(null);
  const [stylePreview, setStylePreview] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const styleInputRef = useRef<HTMLInputElement>(null);
  const [manualForm, setManualForm] = useState({ label: '', description: '', promptT2i: '', promptI2i: '' });
  const [aiStep, setAiStep] = useState<'upload' | 'review'>('upload');
  const [refineInstruction, setRefineInstruction] = useState('');
  const [refining, setRefining] = useState(false);

  // Editar prompts.
  const [editStyleId, setEditStyleId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ label: '', description: '', promptI2i: '', promptT2i: '', builtin: false });
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  // Thumbnail nuevo elegido en el editor ('' = mantener el actual).
  const [editThumbUrl, setEditThumbUrl] = useState('');
  const [editThumbUploading, setEditThumbUploading] = useState(false);
  const editThumbInputRef = useRef<HTMLInputElement>(null);
  const t2iRef = useRef<HTMLTextAreaElement>(null);
  const i2iRef = useRef<HTMLTextAreaElement>(null);
  const [activeField, setActiveField] = useState<'promptT2i' | 'promptI2i'>('promptT2i');

  // Probar el estilo (poster t2i de muestra).
  const [sampleTopic, setSampleTopic] = useState('');
  const [sampleUrl, setSampleUrl] = useState('');
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleProgress, setSampleProgress] = useState('');
  const [sampleError, setSampleError] = useState('');
  // Colores reales de la referencia recién analizada (para previsualizar el
  // sample en la paleta original; '' = usar los colores del brand).
  const [aiReferenceColors, setAiReferenceColors] = useState('');
  // URL del sample elegido como thumbnail del estilo nuevo ('' = usar la imagen subida).
  const [thumbSampleUrl, setThumbSampleUrl] = useState('');
  const sampleAbortRef = useRef(false);
  const aiOriginalRef = useRef<{ label: string; description: string; promptT2i: string; promptI2i: string } | null>(null);
  const editOriginalRef = useRef<{ label: string; description: string; promptT2i: string; promptI2i: string } | null>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canEditPrompts = user?.role === 'mod' || isAdmin;

  const loadStyles = useCallback(async () => {
    const data = await apiClient.get<PresetsResponse>('/api/photo-stylize/presets');
    setPresets(data.presets);
  }, []);

  useEffect(() => { return () => { sampleAbortRef.current = true; }; }, []);

  // Auto-abrir el modal de creación al entrar con ?new=1 (lo usa el admin desde
  // /admin/styles para crear un estilo usando este workspace como contexto).
  useEffect(() => {
    if (searchParams.get('new') === '1') {
      openNewStyle();
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  useEffect(() => { loadStyles().catch((err) => setError(err.message || 'Failed to load styles')); }, [loadStyles]);

  function resetSample() {
    sampleAbortRef.current = true;
    setSampleUrl(''); setSampleError(''); setSampleProgress(''); setSampleLoading(false); setSampleTopic('');
  }

  async function generateSample(promptT2i: string, colors?: string) {
    if (!promptT2i.trim()) { setSampleError('No poster prompt to sample.'); return; }
    sampleAbortRef.current = false;
    setSampleLoading(true); setSampleError(''); setSampleUrl(''); setSampleProgress('Generating sample… ~40-60s');
    try {
      const init = await apiClient.post<{ taskId: string; aspectRatio: string }>(
        `/api/workspaces/${workspaceId}/photo-stylize/styles/sample`,
        { promptT2i, topic: sampleTopic.trim() || undefined, colors: colors?.trim() || undefined },
      );
      const maxAttempts = 60; const intervalMs = 3000;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (sampleAbortRef.current) return;
        await new Promise((r) => setTimeout(r, intervalMs));
        if (sampleAbortRef.current) return;
        const status = await apiClient.get<StylizeStatusResponse>(
          `/api/workspaces/${workspaceId}/photo-stylize/status/${init.taskId}`,
        );
        if (status.state === 'success' && status.resultUrl) { setSampleUrl(status.resultUrl); setSampleProgress(''); setSampleLoading(false); return; }
        if (status.state === 'fail') throw new Error(status.error || 'Generation failed');
        setSampleProgress(`Generating sample… ${Math.round((attempt * intervalMs) / 1000)}s`);
      }
      throw new Error('Took too long. Try again.');
    } catch (err) {
      if (sampleAbortRef.current) return;
      setSampleError(err instanceof Error ? err.message : 'Sample failed');
      setSampleProgress(''); setSampleLoading(false);
    }
  }

  function renderSampleBlock(promptT2i: string, onRevert: () => void, canRevert: boolean, colors?: string, onUseAsThumb?: () => void) {
    return (
      <Section>
        <Panel>
          <SectionLabel>Test the style</SectionLabel>
          <Hint>
            Generates a sample poster with this workspace's brand (uses the poster prompt). Cost: 1 generation, ~40-60s.
          </Hint>
          <Row>
            <TextInput value={sampleTopic} onChange={(e) => setSampleTopic(e.target.value)}
              placeholder="Example topic (optional): e.g. '2-for-1 on haircuts'" disabled={sampleLoading}
              style={{ flex: '1 1 200px', width: 'auto' }} />
            <Button type="button" $variant="secondary" onClick={() => generateSample(promptT2i, colors)} disabled={sampleLoading || !promptT2i.trim()}>
              {sampleLoading ? 'Generating…' : 'Generate sample'}
            </Button>
            <Button type="button" $variant="secondary" onClick={onRevert} disabled={!canRevert || sampleLoading} title="Revert to the original prompts">
              Revert changes
            </Button>
          </Row>
          {sampleProgress && <MutedNote>{sampleProgress}</MutedNote>}
          {sampleError && <ErrorBox>{sampleError}</ErrorBox>}
          {sampleUrl && (
            <div style={{ marginTop: 10 }}>
              <SampleImg src={sampleUrl} alt="sample" />
              {onUseAsThumb && (
                <Button type="button" $variant="secondary" style={{ marginTop: 8 }} onClick={onUseAsThumb} disabled={sampleLoading}>
                  {thumbSampleUrl === sampleUrl ? '✓ Using sample as thumbnail' : 'Use sample as thumbnail'}
                </Button>
              )}
            </div>
          )}
        </Panel>
      </Section>
    );
  }

  function insertVariableInto<T extends { promptT2i: string; promptI2i: string }>(token: string, setForm: React.Dispatch<React.SetStateAction<T>>) {
    const ref = activeField === 'promptI2i' ? i2iRef : t2iRef;
    const el = ref.current;
    const start = el?.selectionStart ?? el?.value.length ?? 0;
    const end = el?.selectionEnd ?? el?.value.length ?? 0;
    setForm((f) => { const cur = f[activeField] || ''; return { ...f, [activeField]: cur.slice(0, start) + token + cur.slice(end) }; });
    requestAnimationFrame(() => { if (!el) return; el.focus(); const pos = start + token.length; el.setSelectionRange(pos, pos); });
  }
  const insertVariable = (token: string) => insertVariableInto(token, setEditForm);

  function openNewStyle() {
    setStyleFile(null); setStylePreview(''); setCreateError(''); setNewStyleMode('ai'); setThumbSampleUrl(''); setAiReferenceColors('');
    setAiStep('upload'); setRefineInstruction('');
    setManualForm({ label: '', description: '', promptT2i: '', promptI2i: '' });
    aiOriginalRef.current = null; resetSample(); setShowNewStyle(true);
  }
  function handleStyleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]; if (!f) return;
    setStyleFile(f); setStylePreview(URL.createObjectURL(f)); setCreateError(''); setAiReferenceColors(''); setThumbSampleUrl('');
  }

  async function handleAnalyze() {
    if (!styleFile) { setCreateError('Pick an example image first.'); return; }
    setCreating(true); setCreateError('');
    try {
      const fd = new FormData(); fd.append('file', styleFile);
      const res = await apiClient.postFormData<{ label: string; description: string; promptT2i: string; promptI2i: string; referenceColors?: string }>(
        `/api/workspaces/${workspaceId}/photo-stylize/styles/analyze`, fd,
      );
      const fresh = { label: res.label || '', description: res.description || '', promptT2i: res.promptT2i || '', promptI2i: res.promptI2i || '' };
      setManualForm(fresh); aiOriginalRef.current = fresh; resetSample(); setAiReferenceColors(res.referenceColors || ''); setAiStep('review');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Analysis failed');
    } finally { setCreating(false); }
  }

  async function handleRefine() {
    const instruction = refineInstruction.trim();
    if (!instruction) { setCreateError('Tell the AI what to adjust.'); return; }
    setRefining(true); setCreateError('');
    try {
      const res = await apiClient.post<{ label: string; description: string; promptT2i: string; promptI2i: string }>(
        `/api/workspaces/${workspaceId}/photo-stylize/styles/refine`,
        { label: manualForm.label, description: manualForm.description, promptT2i: manualForm.promptT2i, promptI2i: manualForm.promptI2i, instruction },
      );
      setManualForm({
        label: res.label || manualForm.label, description: res.description || manualForm.description,
        promptT2i: res.promptT2i || manualForm.promptT2i, promptI2i: res.promptI2i || manualForm.promptI2i,
      });
      setRefineInstruction('');
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Refine failed');
    } finally { setRefining(false); }
  }

  async function handleCreateManual() {
    if (!manualForm.label.trim()) { setCreateError('Enter a name.'); return; }
    if (!manualForm.promptT2i.trim() && !manualForm.promptI2i.trim()) { setCreateError('Enter at least one prompt (poster or photo).'); return; }
    setCreating(true); setCreateError('');
    try {
      const fd = new FormData();
      fd.append('label', manualForm.label.trim());
      fd.append('description', manualForm.description.trim());
      fd.append('promptT2i', manualForm.promptT2i.trim());
      fd.append('promptI2i', manualForm.promptI2i.trim());
      if (styleFile) fd.append('file', styleFile);
      // Si el user eligió un sample como thumbnail, esa URL (ya en R2) pisa la imagen subida.
      if (thumbSampleUrl) fd.append('previewUrl', thumbSampleUrl);
      await apiClient.postFormData(`/api/workspaces/${workspaceId}/photo-stylize/styles/manual`, fd);
      await loadStyles();
      setShowNewStyle(false);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create style');
    } finally { setCreating(false); }
  }

  async function openEditStyle(styleId: string, e: React.MouseEvent) {
    e.stopPropagation();
    setEditStyleId(styleId); setEditError(''); setEditLoading(true);
    editOriginalRef.current = null; resetSample(); setEditThumbUrl('');
    setEditForm({ label: '', description: '', promptI2i: '', promptT2i: '', builtin: false });
    try {
      const data = await apiClient.get<{ label: string; description: string; promptI2i: string; promptT2i: string; builtin: boolean }>(
        `/api/workspaces/${workspaceId}/photo-stylize/styles/${styleId}/prompts`,
      );
      setEditForm({ label: data.label || '', description: data.description || '', promptI2i: data.promptI2i || '', promptT2i: data.promptT2i || '', builtin: !!data.builtin });
      editOriginalRef.current = { label: data.label || '', description: data.description || '', promptT2i: data.promptT2i || '', promptI2i: data.promptI2i || '' };
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to load prompts');
    } finally { setEditLoading(false); }
  }

  // Sube una imagen como thumbnail del estilo (a R2 vía background-images) y la
  // deja elegida; saveEditStyle la persiste como preview_url.
  async function uploadEditThumb(file: File) {
    setEditThumbUploading(true); setEditError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const img = await apiClient.upload<{ url: string }>(`/api/workspaces/${workspaceId}/background-images/upload`, fd);
      if (img?.url) setEditThumbUrl(img.url);
    } catch {
      setEditError('Failed to upload the thumbnail. Try another image.');
    } finally { setEditThumbUploading(false); }
  }

  async function saveEditStyle() {
    if (!editStyleId) return;
    setEditSaving(true); setEditError('');
    try {
      await apiClient.put(`/api/workspaces/${workspaceId}/photo-stylize/styles/${editStyleId}/prompts`, {
        label: editForm.label, description: editForm.description, promptI2i: editForm.promptI2i, promptT2i: editForm.promptT2i,
        ...(editThumbUrl ? { previewUrl: editThumbUrl } : {}),
      });
      await loadStyles(); setEditStyleId(null);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to save');
    } finally { setEditSaving(false); }
  }

  async function handleDeleteStyle(styleId: string, e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm('Delete this custom style? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/photo-stylize/styles/${styleId}`);
      await loadStyles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete style');
    }
  }

  return (
    <>
      {error && <ErrorBox>{error}</ErrorBox>}
      <Section style={{ marginTop: 0 }}>
        <PresetGrid>
          {presets.map((p) => {
            const canDelete = p.custom && (isAdmin || (!!user && p.createdBy === user.id));
            return (
              <PresetCard key={p.id}>
                {canEditPrompts && (
                  <EditStyleBtn type="button" title="Edit prompts" onClick={(e) => openEditStyle(p.id, e)}>✎</EditStyleBtn>
                )}
                {canDelete && (
                  <DeleteStyleBtn type="button" title="Delete style" onClick={(e) => handleDeleteStyle(p.id, e)}>×</DeleteStyleBtn>
                )}
                <span className="preview">
                  {p.previewPath ? <img src={p.previewPath} alt={`${p.label} preview`} loading="lazy" /> : null}
                </span>
                <span className="body">
                  <h3>{p.label}{p.custom && <CustomBadge>Custom</CustomBadge>}</h3>
                  <p>{p.description}</p>
                </span>
              </PresetCard>
            );
          })}
          <NewStyleCard type="button" onClick={openNewStyle}>
            <span className="plus">+</span>
            <strong>New style</strong>
            <small>Upload an example image and the AI learns the style, or enter it by hand.</small>
          </NewStyleCard>
        </PresetGrid>
      </Section>

      {/* ===== Modal: nuevo estilo ===== */}
      {showNewStyle && (
        <ModalOverlay onClick={() => !creating && setShowNewStyle(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()} style={{ maxWidth: (newStyleMode === 'manual' || (newStyleMode === 'ai' && aiStep === 'review')) ? 640 : undefined }}>
            <h2>New style</h2>
            <ModeTabs>
              <ModeTab type="button" $active={newStyleMode === 'ai'} onClick={() => setNewStyleMode('ai')}>From an image (AI)</ModeTab>
              <ModeTab type="button" $active={newStyleMode === 'manual'} onClick={() => setNewStyleMode('manual')}>Manual</ModeTab>
            </ModeTabs>

            {newStyleMode === 'ai' && aiStep === 'upload' ? (
              <>
                <p>Upload an image that represents the style (a poster, an illustration, an ad…). The AI analyzes it and you review the result before saving. Shared across all workspaces.</p>
                <DropZone $hasFile={!!styleFile} onClick={() => styleInputRef.current?.click()}>
                  {stylePreview ? <PreviewImg src={stylePreview} alt="style example" /> : <div><p>Click to choose an image (JPG, PNG or WebP, up to 5MB)</p></div>}
                </DropZone>
                {createError && <ErrorBox>{createError}</ErrorBox>}
                <Actions>
                  <Button onClick={handleAnalyze} disabled={!styleFile || creating}>{creating ? 'Analyzing…' : 'Analyze image'}</Button>
                  <Button onClick={() => setShowNewStyle(false)} $variant="secondary" disabled={creating}>Cancel</Button>
                </Actions>
              </>
            ) : newStyleMode === 'ai' && aiStep === 'review' ? (
              <>
                <p>Review what the AI proposed. Edit by hand, insert brand variables, or ask the AI to adjust it. Then approve to save.</p>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  {stylePreview && <ThumbImg src={stylePreview} alt="example" />}
                  <div style={{ flex: 1 }}>
                    <SectionLabel>Name</SectionLabel>
                    <TextInput value={manualForm.label} onChange={(e) => setManualForm((f) => ({ ...f, label: e.target.value }))} />
                    <SectionLabel style={{ marginTop: 12 }}>Description</SectionLabel>
                    <TextInput value={manualForm.description} onChange={(e) => setManualForm((f) => ({ ...f, description: e.target.value }))} />
                  </div>
                </div>
                <Section>
                  <VariablePicker
                    helpText="Click the textarea where you want it and pick one. Brand data is injected at generation time."
                    onInsert={(token) => insertVariableInto(token, setManualForm)}
                  />
                </Section>
                <Section>
                  <SectionLabel>Poster prompt (text-to-image)</SectionLabel>
                  <PromptTextarea ref={t2iRef} onFocus={() => setActiveField('promptT2i')} value={manualForm.promptT2i} onChange={(e) => setManualForm((f) => ({ ...f, promptT2i: e.target.value }))} placeholder="Design a ... poster for {{brand}} about {{topic}} ..." />
                </Section>
                <Section>
                  <SectionLabel>Photo-as-base prompt (image-to-image)</SectionLabel>
                  <PromptTextarea ref={i2iRef} onFocus={() => setActiveField('promptI2i')} value={manualForm.promptI2i} onChange={(e) => setManualForm((f) => ({ ...f, promptI2i: e.target.value }))} placeholder="Transform the person in the PROVIDED PHOTO into ..." />
                </Section>
                <Section>
                  <SectionLabel>Improve with AI</SectionLabel>
                  <Row style={{ flexWrap: 'nowrap' }}>
                    <TextInput value={refineInstruction} onChange={(e) => setRefineInstruction(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleRefine(); } }} placeholder="E.g.: more dramatic, add {{phone}} at the bottom, warmer palette…" disabled={refining} style={{ flex: 1 }} />
                    <Button onClick={handleRefine} $variant="secondary" disabled={refining || !refineInstruction.trim()}>{refining ? 'Improving…' : 'Improve'}</Button>
                  </Row>
                </Section>
                {renderSampleBlock(manualForm.promptT2i, () => { if (aiOriginalRef.current) setManualForm({ ...aiOriginalRef.current }); resetSample(); }, !!aiOriginalRef.current, aiReferenceColors, () => setThumbSampleUrl(sampleUrl))}
                {createError && <ErrorBox>{createError}</ErrorBox>}
                <Actions>
                  <Button onClick={handleCreateManual} disabled={creating || refining}>{creating ? 'Saving…' : 'Approve and save'}</Button>
                  <Button onClick={() => { setAiStep('upload'); setCreateError(''); }} $variant="secondary" disabled={creating || refining}>← Back</Button>
                </Actions>
              </>
            ) : (
              <>
                <p>Write the prompts by hand. Use placeholders like <code>{'{{brand}}'}</code>, <code>{'{{topic}}'}</code>, <code>{'{{colors}}'}</code>, <code>{'{{phone}}'}</code>. The contact info + the anti-invention rule are added automatically. Shared across all workspaces.</p>
                <Section style={{ marginTop: 0 }}>
                  <SectionLabel>Name</SectionLabel>
                  <TextInput value={manualForm.label} onChange={(e) => setManualForm((f) => ({ ...f, label: e.target.value }))} />
                </Section>
                <Section>
                  <SectionLabel>Description</SectionLabel>
                  <TextInput value={manualForm.description} onChange={(e) => setManualForm((f) => ({ ...f, description: e.target.value }))} />
                </Section>
                <Section>
                  <SectionLabel>Poster prompt (text-to-image)</SectionLabel>
                  <PromptTextarea value={manualForm.promptT2i} onChange={(e) => setManualForm((f) => ({ ...f, promptT2i: e.target.value }))} placeholder="Design a ... poster for {{brand}} about {{topic}} ..." />
                </Section>
                <Section>
                  <SectionLabel>Photo-as-base prompt (image-to-image)</SectionLabel>
                  <PromptTextarea value={manualForm.promptI2i} onChange={(e) => setManualForm((f) => ({ ...f, promptI2i: e.target.value }))} placeholder="Transform the person in the PROVIDED PHOTO into ..." />
                </Section>
                <Section>
                  <SectionLabel>Preview image (optional)</SectionLabel>
                  <DropZone $hasFile={!!styleFile} onClick={() => styleInputRef.current?.click()}>
                    {stylePreview ? <PreviewImg src={stylePreview} alt="preview" /> : <div><p>Click to add a thumbnail (optional)</p></div>}
                  </DropZone>
                </Section>
                {createError && <ErrorBox>{createError}</ErrorBox>}
                <Actions>
                  <Button onClick={handleCreateManual} disabled={creating}>{creating ? 'Saving…' : 'Create style'}</Button>
                  <Button onClick={() => setShowNewStyle(false)} $variant="secondary" disabled={creating}>Cancel</Button>
                </Actions>
              </>
            )}
            <HiddenInput ref={styleInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleStyleFileChange} />
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ===== Modal: editar prompts ===== */}
      {editStyleId && (
        <ModalOverlay onClick={() => !editSaving && setEditStyleId(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <h2>Edit style prompts {editForm.builtin && <CustomBadge>Built-in</CustomBadge>}</h2>
            <p>These prompts drive the AI-styled posters (QuickGen / Novah). Use placeholders: <code>{'{{brand}}'}</code>, <code>{'{{topic}}'}</code>, <code>{'{{industry}}'}</code> and contact ones like <code>{'{{phone}}'}</code>. The contact info + the anti-invention rule are added automatically. Shared across all workspaces.</p>
            {editLoading ? (
              <MutedNote>Loading…</MutedNote>
            ) : (
              <>
                <Section style={{ marginTop: 0 }}>
                  <SectionLabel>Name</SectionLabel>
                  <TextInput value={editForm.label} onChange={(e) => setEditForm((f) => ({ ...f, label: e.target.value }))} />
                </Section>
                <Section>
                  <SectionLabel>Description</SectionLabel>
                  <TextInput value={editForm.description} onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))} />
                </Section>
                <Section>
                  <SectionLabel>Thumbnail</SectionLabel>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {(editThumbUrl || presets.find((p) => p.id === editStyleId)?.previewPath) ? (
                      <EditThumb
                        src={editThumbUrl || presets.find((p) => p.id === editStyleId)?.previewPath}
                        alt="thumbnail"
                      />
                    ) : (
                      <NoThumb>none</NoThumb>
                    )}
                    <div>
                      <input
                        ref={editThumbInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadEditThumb(f); e.target.value = ''; }}
                      />
                      <Button type="button" $variant="secondary" onClick={() => editThumbInputRef.current?.click()} disabled={editThumbUploading}>
                        {editThumbUploading ? 'Uploading…' : 'Upload image'}
                      </Button>
                      {editThumbUrl && <SuccessNote>New thumbnail ready — hit Save</SuccessNote>}
                    </div>
                  </div>
                </Section>
                <Section>
                  <VariablePicker
                    helpText="Insert brand data into the prompt. Click the textarea where you want it and pick one."
                    onInsert={insertVariable}
                  />
                </Section>
                <Section>
                  <SectionLabel>Poster prompt (text-to-image)</SectionLabel>
                  <PromptTextarea ref={t2iRef} onFocus={() => setActiveField('promptT2i')} value={editForm.promptT2i} onChange={(e) => setEditForm((f) => ({ ...f, promptT2i: e.target.value }))} placeholder="Design a ... poster for {{brand}} about {{topic}} ..." />
                </Section>
                <Section>
                  <SectionLabel>Photo-as-base prompt (image-to-image)</SectionLabel>
                  <PromptTextarea ref={i2iRef} onFocus={() => setActiveField('promptI2i')} value={editForm.promptI2i} onChange={(e) => setEditForm((f) => ({ ...f, promptI2i: e.target.value }))} placeholder="Transform the person in the PROVIDED PHOTO into ..." />
                </Section>
                {renderSampleBlock(editForm.promptT2i, () => { const o = editOriginalRef.current; if (o) setEditForm((f) => ({ ...f, ...o })); resetSample(); }, !!editOriginalRef.current, undefined, () => { setThumbSampleUrl(sampleUrl); setEditThumbUrl(sampleUrl); })}
              </>
            )}
            {editError && <ErrorBox>{editError}</ErrorBox>}
            <Actions>
              <Button onClick={saveEditStyle} disabled={editLoading || editSaving}>{editSaving ? 'Saving…' : 'Save prompts'}</Button>
              <Button onClick={() => setEditStyleId(null)} $variant="secondary" disabled={editSaving}>Cancel</Button>
            </Actions>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
}
